import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ChatBubbleIcon } from '@rsos/assets/icons';
import { Box } from '@rsos/components/capstone/base';
import { useLazyEndConversationQuery } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import { useSelectedTextFrom911Conversation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import { TF911_CONVERSATION_ENDED } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import { selectUserOrgByApp } from '@rsos/sinatra';
import {
  trackEndTF911Click,
  trackEndTF911ModalClick,
  trackEndTF911Response,
} from '../textFrom911Trackings';
import { EndChatButtonNext, EndChatContainer } from './TF911.styles';
import TF911EndChatModal from './TF911EndChatModal';

const TF911EndChat = ({
  callerID,
  isForOverview,
  isUniteEventPaneEnabled,
  gpxFeature,
}) => {
  const [showEndConversationModal, setShowConversationModal] = useState(false);

  const orgName = useSelector(
    state =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole.application,
      ).name,
  );
  const conversation = useSelectedTextFrom911Conversation(callerID);

  const isEndingConversation = useMemo(
    () => conversation?.loading?.endConversation,
    [conversation],
  );

  const isConversationEnded = useMemo(
    () => conversation?.data?.custom?.STATUS === TF911_CONVERSATION_ENDED,
    [conversation],
  );

  const [triggerEndConversation] = useLazyEndConversationQuery();

  const handleEndConversation = useCallback(() => {
    trackEndTF911ModalClick({
      feature: gpxFeature,
      fromOverviewTab: isForOverview,
      isUniteEventPaneEnabled: isUniteEventPaneEnabled,
      isConfirmed: true,
      conversationId: conversation?.data?.conversation_id,
    });

    triggerEndConversation({ callerID, orgName }).then(response => {
      trackEndTF911Response({
        feature: gpxFeature,
        responseSuccess: !response?.error,
        conversationId: conversation?.data?.conversation_id,
      });
      setShowConversationModal(false);
    });
  }, [
    callerID,
    orgName,
    triggerEndConversation,
    isForOverview,
    isUniteEventPaneEnabled,
    gpxFeature,
    conversation,
  ]);

  const handleEndChatBtnClick = useCallback(() => {
    // track click on cancel in end chat modal
    if (showEndConversationModal) {
      trackEndTF911ModalClick({
        feature: gpxFeature,
        fromOverviewTab: isForOverview,
        isUniteEventPaneEnabled: isUniteEventPaneEnabled,
        isConfirmed: false,
        conversationId: conversation?.data?.conversation_id,
      });
    } else {
      // track click on end chat button
      trackEndTF911Click({
        feature: gpxFeature,
        fromOverviewTab: isForOverview,
        isUniteEventPaneEnabled: isUniteEventPaneEnabled,
        conversationId: conversation?.data?.conversation_id,
      });
    }
    setShowConversationModal(prev => !prev);
  }, [
    showEndConversationModal,
    gpxFeature,
    isForOverview,
    isUniteEventPaneEnabled,
    conversation,
  ]);

  return (
    <EndChatContainer>
      <Box
        alignSelf="center"
        borderRadius="0 0 5px 5px"
        horizontal
        width="100%"
      >
        <EndChatButtonNext
          btnType="warning_tertiary"
          disabled={isEndingConversation || isConversationEnded}
          isLoadingProp={isEndingConversation}
          noMargin
          onClick={handleEndChatBtnClick}
          size="med"
          grow
          width="100%"
        >
          <ChatBubbleIcon color="lightDanger" />
          End Chat
        </EndChatButtonNext>
      </Box>

      <TF911EndChatModal
        callerID={callerID}
        isOpened={showEndConversationModal}
        isLoading={isEndingConversation}
        onClose={handleEndChatBtnClick}
        endChat={handleEndConversation}
      />
    </EndChatContainer>
  );
};

export default TF911EndChat;
